import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Container, Users, Award, Shield, Clock, DollarSign, TowerControl, Warehouse, Star, Phone } from 'lucide-react';
import { services, whyChooseUs, stats, companyInfo } from '../mock';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  crane: TowerControl,
  truck: Truck,
  container: Container,
  users: Users,
  award: Award,
  'users-2': Users,
  clock: Clock,
  'dollar-sign': DollarSign,
  'shield-check': Shield,
  warehouse: Warehouse
};

const serviceSlugMap = {
  1: 'lorry-crane',
  2: 'tailgate',
  3: 'trailer',
  4: 'manpower',
  5: 'warehouse-storage'
};

const serviceImages = {
  1: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/7gbywntd_image.png',
  3: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_6ad06afe-da3b-46ca-832e-3147a77af072/artifacts/551hr9yw_image.png',
  4: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/cgsbbfqb_WhatsApp%20Image%202026-02-02%20at%2011.01.59%20AM.jpeg',
  5: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/15zfg1g2_WhatsApp%20Image%202026-01-27%20at%203.47.06%20PM.jpeg',
};

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/testimonials?visible_only=true`)
      .then(res => setTestimonials(res.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-burgundy-deep">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[92vh] flex items-end pt-32 pb-14 sm:pb-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5961982/pexels-photo-5961982.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="NJK Logistics heavy machinery transport"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-burgundy-deep/95 via-burgundy-deep/70 to-burgundy-deep/30" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-burgundy-deep/90 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <p className="animate-fade-up text-gold text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] mb-5">
              Singapore · Heavy Vehicle Logistics
            </p>
            <h1 className="animate-fade-up font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-none mb-6" style={{ animationDelay: '0.1s' }}>
              Moving What Others
              <span className="text-gold italic"> Can't.</span>
            </h1>
            <p className="animate-fade-up text-white/75 text-base md:text-lg leading-relaxed max-w-xl mb-9" style={{ animationDelay: '0.2s' }}>
              Lorry cranes up to 100 tons, trailers, tailgates and warehouse storage — operated by certified professionals. Swift, secure, and reliable since day one.
            </p>
            <div className="animate-fade-up flex flex-wrap gap-4" style={{ animationDelay: '0.3s' }}>
              <Link to="/booking" data-testid="hero-book-now-btn">
                <button className="bg-gold text-burgundy-deep font-semibold uppercase tracking-[0.1em] text-sm px-8 py-4 rounded-md hover:bg-gold-light transition-colors inline-flex items-center group">
                  Book a Service
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </button>
              </Link>
              <Link to="/contact" data-testid="hero-quote-btn">
                <button className="border border-white/40 text-white font-medium uppercase tracking-[0.1em] text-sm px-8 py-4 rounded-md hover:bg-white hover:text-burgundy-deep transition-colors">
                  Get a Quote
                </button>
              </Link>
            </div>

            {/* Hero stats strip */}
            <div className="animate-fade-up mt-14 grid grid-cols-3 gap-6 border-t border-gold/20 pt-8" style={{ animationDelay: '0.4s' }}>
              {stats.map((stat) => (
                <div key={stat.label} data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="font-display text-3xl sm:text-4xl font-bold text-gold">{stat.value}</p>
                  <p className="text-white/60 text-xs sm:text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="py-20 md:py-28 px-4" data-testid="services-section">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-gold text-xs font-semibold uppercase tracking-[0.3em] mb-4">What We Do</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                Built for Heavy Lifting
              </h2>
            </div>
            <Link to="/services" className="text-gold font-medium text-sm uppercase tracking-[0.15em] inline-flex items-center group shrink-0" data-testid="services-view-all-link">
              All Services
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
            {services.map((service, idx) => {
              const Icon = iconMap[service.icon];
              const slug = serviceSlugMap[service.id];
              const isFeature = service.id === 1;
              const img = serviceImages[service.id];
              return (
                <Link
                  key={service.id}
                  to={`/services/${slug}`}
                  data-testid={`service-card-${slug}`}
                  className={`group relative rounded-xl overflow-hidden border border-gold/20 bg-burgundy-dark transition-shadow duration-300 hover:shadow-xl hover:shadow-black/40 hover:border-gold/40 ${
                    isFeature ? 'lg:col-span-6 lg:row-span-2 min-h-[320px]' : 'lg:col-span-3 min-h-[240px]'
                  }`}
                >
                  {img && (
                    <div className="absolute inset-0 overflow-hidden">
                      <img src={img} alt={service.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-burgundy-deep/95 via-burgundy-deep/50 to-burgundy-deep/20" />
                    </div>
                  )}
                  <div className={`relative z-10 h-full flex flex-col justify-end p-6 md:p-8 ${!img ? 'justify-between' : ''}`}>
                    {!img && (
                      <div className="w-12 h-12 bg-gold/15 rounded-lg flex items-center justify-center mb-6 group-hover:bg-gold transition-colors">
                        <Icon className="text-gold group-hover:text-burgundy-deep transition-colors" size={24} strokeWidth={1.5} />
                      </div>
                    )}
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-[0.2em] mb-2 text-gold`}>
                        {service.capacity}
                      </p>
                      <h3 className={`font-display text-xl md:text-2xl font-bold tracking-tight mb-2 text-white`}>
                        {service.name}
                      </h3>
                      <p className={`text-sm leading-relaxed mb-4 text-white/65`}>
                        {service.description}
                      </p>
                      <span className={`inline-flex items-center text-sm font-medium text-gold`}>
                        Learn More
                        <ArrowRight className="ml-1.5 group-hover:translate-x-1 transition-transform" size={15} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE NJK ============ */}
      <section className="py-20 md:py-28 px-4 bg-burgundy-dark grain-overlay relative" data-testid="why-choose-section">
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-gold text-xs font-semibold uppercase tracking-[0.3em] mb-4">Why NJK</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight mb-6">
                A Partner You Can Count On
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                We deliver excellence through experience, professionalism, and an uncompromising commitment to safety.
              </p>
              <a href={`tel:${companyInfo.phone}`} className="inline-flex items-center gap-3 text-gold hover:text-gold-light transition-colors" data-testid="why-choose-phone-link">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center">
                  <Phone size={17} />
                </span>
                <span>
                  <span className="block text-xs text-white/50 uppercase tracking-[0.2em]">Call us anytime</span>
                  <span className="font-semibold">{companyInfo.phone}</span>
                </span>
              </a>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-gold/15 rounded-xl overflow-hidden border border-gold/15">
              {whyChooseUs.map((item) => {
                const Icon = iconMap[item.icon];
                return (
                  <div key={item.title} className="bg-burgundy-dark p-7 md:p-9 hover:bg-burgundy transition-colors">
                    <Icon className="text-gold mb-5" size={26} strokeWidth={1.5} />
                    <h3 className="font-display text-lg font-bold text-white mb-2 tracking-tight">{item.title}</h3>
                    <p className="text-white/55 text-sm leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-28 px-4" data-testid="testimonials-section">
          <div className="container mx-auto">
            <div className="mb-14">
              <p className="text-gold text-xs font-semibold uppercase tracking-[0.3em] mb-4">Testimonials</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                Trusted Across Singapore
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 6).map((testimonial) => (
                <div key={testimonial.id} className="bg-burgundy-dark rounded-xl border border-gold/20 p-8 hover:border-gold/40 transition-colors duration-300" data-testid={`testimonial-${testimonial.id}`}>
                  <div className="flex mb-5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i}`} className="w-4 h-4 text-gold fill-gold" />
                    ))}
                  </div>
                  <p className="font-display text-white/85 italic leading-relaxed mb-6">"{testimonial.text}"</p>
                  <div className="border-t border-gold/15 pt-4">
                    <p className="font-semibold text-gold text-sm">{testimonial.name}</p>
                    {testimonial.company && <p className="text-xs text-white/50 mt-0.5">{testimonial.company}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ CTA ============ */}
      <section className="relative py-24 md:py-32 px-4 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1602891382287-daa85a949cd1?auto=format&fit=crop&w=1920&q=70"
            alt="Singapore cityscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-burgundy-deep/85" />
        </div>
        <div className="container mx-auto relative z-10 max-w-3xl text-center">
          <p className="text-gold text-xs font-semibold uppercase tracking-[0.3em] mb-5">Ready When You Are</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight mb-6">
            Let's Move Your Next Project
          </h2>
          <p className="text-white/70 text-base md:text-lg mb-10 leading-relaxed">
            Book online in minutes or request a free quotation — our team responds fast.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/booking" data-testid="cta-book-now-btn">
              <button className="bg-gold text-burgundy-deep font-semibold uppercase tracking-[0.1em] text-sm px-8 py-4 rounded-md hover:bg-gold-light transition-colors">
                Book Now
              </button>
            </Link>
            <Link to="/contact" data-testid="cta-quote-btn">
              <button className="border border-white/40 text-white font-medium uppercase tracking-[0.1em] text-sm px-8 py-4 rounded-md hover:bg-white hover:text-burgundy-deep transition-colors">
                Get a Quote
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
